import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface Customer {
  id: string;
  name: string;
  last_name: string;
  customer_type: string;
  business_name?: string | null;
  representative_first_name?: string | null;
  representative_last_name?: string | null;
  cell_numbers: string[];
  emails: string[];
  city?: string;
  province_state?: string;
  suburb?: string;
  postal_code?: string;
  street_address?: string;
  lead_source?: string;
  lead_source_description?: string;
  lead_owner_id?: string;
  referred_by_id?: string;
  tags?: string[];
}

export interface Invoice {
  id: string;
  invoice_number?: string | null;
  title?: string;
  status: string;
  invoice_type?: string | null;
  percentage_of_estimate?: number | null;
  custom_amount?: number | null;
  total_amount?: number | null;
  created_at: string;
  due_date?: string | null;
  estimate_id?: string;
  customer_id: string;
  customers?: Customer;
  estimates?: any;
}

export const useInvoiceData = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchInvoices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers (
            id,
            name,
            last_name,
            customer_type,
            business_name,
            representative_first_name,
            representative_last_name,
            cell_numbers,
            emails,
            city,
            province_state,
            suburb,
            postal_code,
            street_address,
            lead_source,
            lead_source_description,
            lead_owner_id,
            referred_by_id,
            tags
          ),
          estimates (
            id,
            estimate_number,
            title
          )
        `)
        .order(sortBy, { ascending: sortDir === 'asc' });

      if (error) {
        console.error('Error fetching invoices:', error);
        return;
      }

      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [sortBy, sortDir]);

  // Apply search to invoices
  const searchedInvoices = useMemo(() => {
    if (!search.trim()) return invoices;

    const searchTerm = search.toLowerCase();
    return invoices.filter(invoice => {
      const customerName = invoice.customers 
        ? `${invoice.customers.name} ${invoice.customers.last_name}`.toLowerCase()
        : '';
      
      return (
        invoice.invoice_number?.toLowerCase().includes(searchTerm) ||
        invoice.title?.toLowerCase().includes(searchTerm) ||
        customerName.includes(searchTerm) ||
        invoice.status.toLowerCase().includes(searchTerm)
      );
    });
  }, [invoices, search]);

  // Initial fetch and re-fetch when sort parameters change
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return {
    invoices: searchedInvoices,
    fetchInvoices,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  };
};